import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import ControlPanelSidebar from "../components/control-panel/ControlPanelSidebar";
import { CollabFlowObject, FlowCell, RawCell } from "../models/collaborate.model";
import {
    setControlPanelStatus,
    setSelectedCellId,
    setSelectedControlPanelComponent,
    selectTools,
    selectFlows,
    selectCells,
    selectCollabApp
} from "../store/slices/node.slice";
import type { ILGraphNode } from "../types/litegraph-update";
import { cellSetup } from "../utils/cell-setup";
import useGlobalVariables from "../utils/global-variables";

// eslint-disable-next-line no-undef
const HomeView: React.FC = () => {
    const { LiteGraph, editor, LGraphGroup, LGraphCanvas } = useGlobalVariables();

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const tools = useSelector(selectTools);
    const flows = useSelector(selectFlows);
    const cells = useSelector(selectCells);
    const collabApp = useSelector(selectCollabApp);

    const getIdentifierToCellsMap = (cellArray: RawCell[]) => {
        return new Map(cellArray.map((cell) => [cell.unique_identifier, cell]));
    };

    const getSortedFlows = (flowData: CollabFlowObject[]): CollabFlowObject[] => {
        const sortedFlows: CollabFlowObject[] = [],
            flowArray = flowData;

        for (const flow of flowArray) {
            const previousIDToFlowCellMap = new Map(
                flow?.cell_list?.map((flowCell) => [flowCell.previous_cell || -1, flowCell])
            );

            const sortedFlowCellList: FlowCell[] = [];

            let previousCellID = -1;
            while (previousIDToFlowCellMap.has(previousCellID)) {
                const sortedFlowCell = previousIDToFlowCellMap.get(previousCellID);

                if (sortedFlowCell) {
                    sortedFlowCellList.push(sortedFlowCell);
                    previousCellID = sortedFlowCell.identifier;
                }
            }

            sortedFlows.push({
                flow: flow.flow,
                cell_list: sortedFlowCellList
            });
        }

        return sortedFlows;
    };

    const populateGroup = (group, cell) => {
        const nucleusCellIdentifierMap = new Map(),
            cellularEncoderIdentifierMap = new Map(),
            cellBreakerIdentifierMap = new Map();

        let nucleusNode: ILGraphNode | null = null,
            cellularEncoder: ILGraphNode | null = null,
            toolsNode: ILGraphNode | null = null,
            memoryNode: ILGraphNode | null = null,
            pathwaysNode: ILGraphNode | null = null,
            cellBreakerNode: ILGraphNode | null = null,
            promptNode: ILGraphNode | null = null;

        const centerX = group.pos[0] + group.size[0] * 0.3333;
        const centerY = group.pos[1] + group.size[1] * 0.45;

        if (LiteGraph) {
            /**
             * Add Nuclues Node
             */
            nucleusNode = LiteGraph.createNode("Main/NucleusNode");
            nucleusNode.removable = false;
            nucleusNode.pos = [centerX, centerY];
            nucleusNode.properties.Goal = cell.goal;
            nucleusNode.properties.Analysis = cell.analysis;
            nucleusNode.onDblClick = (e) => {
                e.preventDefault();

                dispatch(setControlPanelStatus("warning"));
                dispatch(setSelectedControlPanelComponent("nucleus"));
                dispatch(setSelectedCellId(cell.unique_identifier));
            };

            editor?.graph.add(nucleusNode);

            nucleusCellIdentifierMap.set(cell.unique_identifier, nucleusNode);

            /**
             * Create Cellular Encoder node Node
             */
            cellularEncoder = LiteGraph.createNode("Main/CellularEncoderNode");
            cellularEncoder.addProperty("group_id", "", "string");
            //@ts-expect-error Need the group_id for easily reconstructing the flow
            cellularEncoder.group_id = cell.unique_identifier;
            cellularEncoder.removable = false;
            cellularEncoder.pos = [centerX, group.pos[1] + 60];

            cellularEncoder.onDblClick = (e) => {
                e.preventDefault();

                dispatch(setControlPanelStatus("warning"));
                dispatch(setSelectedControlPanelComponent("cellEncoder"));
                dispatch(setSelectedCellId(cell.unique_identifier));
            };

            editor?.graph.add(cellularEncoder);

            if (nucleusNode) {
                cellularEncoder.connect(0, nucleusNode, 0);
                // push encoder to list
                cellularEncoderIdentifierMap.set(cell.unique_identifier, cellularEncoder);
            }

            /**
             * Create tools Node
             */
            toolsNode = LiteGraph.createNode("Main/ToolsNode");
            toolsNode.pos = [centerX - 250, centerY];

            toolsNode.title = "Capabilities (Tools)";
            toolsNode.removable = false;
            editor?.graph.add(toolsNode);

            toolsNode.onDblClick = (e) => {
                e.preventDefault();

                /**
                 * Route to Tools view
                 */
                const urlParams = new URLSearchParams(location.search);

                urlParams.set("cell", cell.unique_identifier.toString());

                navigate(`/capabilities?${urlParams.toString()}`);
            };

            // Connect nucleus node to tools node
            if (nucleusNode) {
                toolsNode.connect(0, nucleusNode, 1);
            }

            if (cell.tool_wishlist && cell.tool_wishlist.length > 0 && toolsNode) {
                toolsNode.addWidget("combo", "Tools", "", () => {}, {
                    values: cell.tool_wishlist.map((wishedTool) => wishedTool.title)
                });
            }

            /**
             * Create Memory controller
             */
            memoryNode = LiteGraph.createNode("Main/MemoriesNode");
            memoryNode.removable = false;
            memoryNode.pos = [centerX - 250, centerY + 200];

            editor?.graph.add(memoryNode);

            if (nucleusNode) {
                memoryNode.connect(0, nucleusNode, 2);
            }

            memoryNode.onDblClick = (e) => {
                e.preventDefault();

                dispatch(setControlPanelStatus("warning"));
                dispatch(setSelectedControlPanelComponent("memories"));
                dispatch(setSelectedCellId(cell.unique_identifier));
            };

            /**
             * Create Prompt controller
             */
            promptNode = LiteGraph.createNode("Main/PromptsNode");
            promptNode.removable = false;
            promptNode.pos = [centerX, centerY + 250];

            editor?.graph.add(promptNode);

            if (nucleusNode) {
                promptNode.connect(0, nucleusNode, 3);
            }

            promptNode.onDblClick = (e) => {
                e.preventDefault();
                dispatch(setControlPanelStatus("warning"));
                dispatch(setSelectedControlPanelComponent("agency"));
                dispatch(setSelectedCellId(cell.unique_identifier));
            };

            /**
             * Create Pathway controller
             */
            pathwaysNode = LiteGraph.createNode("Main/PathwaysNode");
            pathwaysNode.removable = false;
            pathwaysNode.pos = [centerX + 250, centerY + 200];

            editor?.graph.add(pathwaysNode);

            if (nucleusNode) {
                pathwaysNode.connect(0, nucleusNode, 4);
            }

            pathwaysNode.onDblClick = (e) => {
                e.preventDefault();

                dispatch(setControlPanelStatus("warning"));
                dispatch(setSelectedControlPanelComponent("neuralPathWays"));
                dispatch(setSelectedCellId(cell.unique_identifier));
            };

            /**
             * Create cell breaker
             */
            cellBreakerNode = LiteGraph.createNode("Main/CellBreakerNode");
            cellBreakerNode.removable = false;
            cellBreakerNode.addProperty("group_id", "", "string");
            //@ts-expect-error Need the group_id for easily reconstructing the flow
            cellBreakerNode.group_id = cell.unique_identifier;
            cellBreakerNode.pos = [centerX + 600, centerY];

            cellBreakerIdentifierMap.set(cell.unique_identifier, cellBreakerNode);
            editor?.graph.add(cellBreakerNode);

            cellBreakerNode.connect(0, cellularEncoder, 0);
            nucleusNode.connect(0, cellBreakerNode, 0);
        }
    };

    const clearAndReinitializeEditor = () => {
        if (editor) {
            editor.graphcanvas.graph.clear();

            const flowData = getSortedFlows(flows);
            const lastFlowData = flowData[flowData.length - 1];
            const lastUniqueCell = lastFlowData.cell_list[lastFlowData.cell_list.length - 1];

            LGraphCanvas.onGroupAdd = function (_info, _entry, mouse_event) {
                const canvas = LGraphCanvas.active_canvas;
                const group = new LGraphGroup();
                group.size = [800, 600]; // Set the size of the group
                group.pos = canvas.convertEventToCanvasOffset(mouse_event);
                group.title = "Cell " + (lastUniqueCell.identifier + 1);
                canvas.graph.add(group);
                populateGroup(group, {
                    unique_identifier: lastUniqueCell.identifier + 1,
                    previous_cell: null
                });
            };
        }
    };

    useEffect(() => {
        /**
         * If the collaboration app is of type rigid, redirect to the capablitlies view
         */
        if (collabApp?.type === "rigid") {
            navigate(`/capabilities${location.search}`);
        }
    }, [collabApp?.type, location.search, navigate]);

    useEffect(() => {
        if (LiteGraph) {
            cellSetup(LiteGraph);
        }
    }, [LiteGraph, editor]);

    useEffect(() => {
        if (editor && tools?.length > 0 && flows.length > 0 && cells.length > 0 && LiteGraph) {
            clearAndReinitializeEditor();

            const nucleusCellIdentifierMap = new Map(),
                cellularEncoderIdentifierMap = new Map(),
                cellBreakerIdentifierMap = new Map(),
                // cellArray: RawCell[] = [],
                initializeNodesArray: any[] = [];

            let startingPositionX = 250,
                startingPosisionY = 150,
                toolStartingPositionX = 0,
                toolStartingPositionY = 0,
                nucleusNode: ILGraphNode | null = null,
                initializeNode: ILGraphNode | null = null,
                cellularEncoder: ILGraphNode | null = null,
                toolsNode: ILGraphNode | null = null,
                memoryNode: ILGraphNode | null = null,
                pathwaysNode: ILGraphNode | null = null,
                cellBreakerNode: ILGraphNode | null = null,
                promptNode: ILGraphNode | null = null,
                nodeSize: [number, number] | [] = [];

            const flowStateData = getSortedFlows(flows);
            const cellsMap = getIdentifierToCellsMap(cells);
            const addedCellsSet = new Set();

            for (let i = 0; i < flowStateData.length; i++) {
                if (flowStateData[i]?.cell_list?.length > 0) {
                    for (let j = 0; j < flowStateData[i].cell_list.length; j++) {
                        if (!addedCellsSet.has(flowStateData[i].cell_list[j].identifier)) {
                            // Add Initializer Node
                            if (j === 0) {
                                initializeNode = LiteGraph.createNode("Main/InitializerNode");

                                initializeNode.removable = false;
                                initializeNode.pos = [10, 200 + startingPosisionY + i * 300];

                                initializeNode.onDblClick = function (e) {
                                    e.preventDefault();
                                };

                                editor?.graph.add(initializeNode);

                                initializeNodesArray.push(initializeNode);
                            }

                            const group = new LGraphGroup();
                            const cell = cellsMap.get(flowStateData[i].cell_list[j].identifier);

                            if (cell) {
                                group.pos = [
                                    startingPositionX + j * 400,
                                    startingPosisionY + i * 300
                                ];
                                group.size = [800, 600]; // Set the size of the group
                                group.title = "Cell " + cell.unique_identifier;
                                group.removable = false;

                                editor?.graph.add(group);

                                const centerX = group.pos[0] + group.size[0] * 0.3333;
                                const centerY = group.pos[1] + group.size[1] * 0.45;

                                /**
                                 * Add Nuclues Node
                                 */
                                nucleusNode = LiteGraph.createNode("Main/NucleusNode");

                                nucleusNode.removable = false;
                                nucleusNode.pos = [centerX, centerY];
                                nucleusNode.properties.Goal = cell.goal;
                                nucleusNode.properties.Analysis = cell.analysis;

                                nucleusNode.onDblClick = (e) => {
                                    e.preventDefault();

                                    dispatch(setControlPanelStatus("warning"));
                                    dispatch(setSelectedControlPanelComponent("nucleus"));
                                    dispatch(setSelectedCellId(cell.unique_identifier));
                                };

                                editor?.graph.add(nucleusNode);

                                nucleusCellIdentifierMap.set(cell.unique_identifier, nucleusNode);

                                toolStartingPositionX = startingPositionX + j * 400 + 20;
                                toolStartingPositionY = centerY;

                                /**
                                 * Create Cellular Encoder node Node
                                 */
                                cellularEncoder = LiteGraph.createNode("Main/CellularEncoderNode");
                                cellularEncoder.addProperty("group_id", "", "string");
                                //@ts-expect-error Need the group_id for easily reconstructing the flow
                                cellularEncoder.group_id = cell.unique_identifier;
                                cellularEncoder.removable = false;
                                cellularEncoder.pos = [centerX, group.pos[1] + 60];

                                cellularEncoder.onDblClick = (e) => {
                                    e.preventDefault();

                                    dispatch(setControlPanelStatus("warning"));
                                    dispatch(setSelectedControlPanelComponent("cellEncoder"));
                                    dispatch(setSelectedCellId(cell.unique_identifier));
                                };

                                editor?.graph.add(cellularEncoder);

                                if (nucleusNode) {
                                    cellularEncoder.connect(0, nucleusNode, 0);
                                    // push encoder to list
                                    cellularEncoderIdentifierMap.set(
                                        cell.unique_identifier,
                                        cellularEncoder
                                    );
                                }

                                /**
                                 * Create tools Node
                                 */
                                toolsNode = LiteGraph.createNode("Main/ToolsNode");
                                toolsNode.pos = [toolStartingPositionX, toolStartingPositionY];

                                toolsNode.title = "Capabilities (Tools)";
                                toolsNode.removable = false;
                                editor?.graph.add(toolsNode);

                                nodeSize = toolsNode.size;

                                toolStartingPositionX += nodeSize[0] + 30;

                                toolsNode.onDblClick = (e) => {
                                    e.preventDefault();

                                    /**
                                     * Route to Tools view
                                     */
                                    const urlParams = new URLSearchParams(location.search);

                                    urlParams.set("cell", cell.unique_identifier.toString());

                                    navigate(`/capabilities?${urlParams.toString()}`);
                                };

                                // Connect nucleus node to tools node
                                if (nucleusNode) {
                                    toolsNode.connect(0, nucleusNode, 1);
                                }

                                if (
                                    cell.tool_wishlist &&
                                    cell.tool_wishlist.length > 0 &&
                                    toolsNode
                                ) {
                                    toolsNode.addWidget("combo", "Tools", "", () => {}, {
                                        values: cell.tool_wishlist.map(
                                            (wishedTool) => wishedTool.title
                                        )
                                    });
                                }

                                /**
                                 * Create Memory controller
                                 */
                                memoryNode = LiteGraph.createNode("Main/MemoriesNode");
                                memoryNode.removable = false;
                                memoryNode.pos = [centerX - 250, centerY + 200];

                                editor?.graph.add(memoryNode);

                                if (nucleusNode) {
                                    memoryNode.connect(0, nucleusNode, 2);
                                }

                                memoryNode.onDblClick = (e) => {
                                    e.preventDefault();

                                    dispatch(setControlPanelStatus("warning"));
                                    dispatch(setSelectedControlPanelComponent("memories"));
                                    dispatch(setSelectedCellId(cell.unique_identifier));
                                };

                                /**
                                 * Create Prompt controller
                                 */
                                promptNode = LiteGraph.createNode("Main/PromptsNode");
                                promptNode.removable = false;
                                promptNode.pos = [centerX, centerY + 250];

                                editor?.graph.add(promptNode);

                                if (nucleusNode) {
                                    promptNode.connect(0, nucleusNode, 3);
                                }

                                promptNode.onDblClick = (e) => {
                                    e.preventDefault();
                                    dispatch(setControlPanelStatus("warning"));
                                    dispatch(setSelectedControlPanelComponent("agency"));
                                    dispatch(setSelectedCellId(cell.unique_identifier));
                                };

                                /**
                                 * Create Pathway controller
                                 */
                                pathwaysNode = LiteGraph.createNode("Main/PathwaysNode");
                                pathwaysNode.removable = false;
                                pathwaysNode.pos = [centerX + 250, centerY + 200];

                                editor?.graph.add(pathwaysNode);

                                if (nucleusNode) {
                                    pathwaysNode.connect(0, nucleusNode, 4);
                                }

                                pathwaysNode.onDblClick = (e) => {
                                    e.preventDefault();

                                    dispatch(setControlPanelStatus("warning"));
                                    dispatch(setSelectedControlPanelComponent("neuralPathWays"));
                                    dispatch(setSelectedCellId(cell.unique_identifier));
                                };

                                /**
                                 * Create cell breaker
                                 */
                                cellBreakerNode = LiteGraph.createNode("Main/CellBreakerNode");
                                cellBreakerNode.addProperty("group_id", "", "string");
                                //@ts-expect-error Need the group_id for easily reconstructing the flow
                                cellBreakerNode.group_id = cell.unique_identifier;
                                cellBreakerNode.removable = false;
                                cellBreakerNode.pos = [
                                    startingPositionX + j * 400 + 900,
                                    startingPosisionY + i * 300 + 300
                                ];

                                cellBreakerIdentifierMap.set(
                                    cell.unique_identifier,
                                    cellBreakerNode
                                );

                                editor?.graph.add(cellBreakerNode);

                                addedCellsSet.add(flowStateData[i].cell_list[j].identifier);
                            }
                        }

                        startingPositionX += 850;
                    }
                }

                startingPosisionY += 650;
                startingPositionX = 250;
            }

            // eslint-disable-next-line no-undef
            localStorage.setItem("initial_data", JSON.stringify(editor?.graph.serialize()));

            if (nucleusCellIdentifierMap.size > 0) {
                const sortedFlows = getSortedFlows(flows);
                const addedCellsConnections = new Set();

                let initializeNodesCounter = 0;

                for (let i = 0; i < sortedFlows.length; i++) {
                    for (let j = 0; j < sortedFlows[i].cell_list.length; j++) {
                        const cellIdentifier = sortedFlows[i].cell_list[j].identifier;
                        const previousIdentifier = sortedFlows[i].cell_list[j - 1]?.identifier;

                        if (!addedCellsConnections.has(cellIdentifier)) {
                            const cellularEncoderNode =
                                cellularEncoderIdentifierMap.get(cellIdentifier);
                            const cellBreakerNode = cellBreakerIdentifierMap.get(cellIdentifier);
                            const previousBreakerNode =
                                cellBreakerIdentifierMap.get(previousIdentifier);
                            const nucleusNode = nucleusCellIdentifierMap.get(cellIdentifier);

                            if (j === 0) {
                                initializeNodesArray[initializeNodesCounter++].connect(
                                    0,
                                    cellularEncoderNode,
                                    1
                                );
                            } else {
                                previousBreakerNode?.connect?.(1, cellularEncoderNode, 1);
                            }

                            cellBreakerNode.connect(0, cellularEncoderNode, 0);
                            cellBreakerNode.currentOutputIndex = 0;
                            nucleusNode.connect(0, cellBreakerNode, 0);

                            addedCellsConnections.add(cellIdentifier);
                        }
                    }
                }
            }
        }
    }, [editor, tools, flows, LiteGraph]);

    return (
        <div>
            <ControlPanelSidebar />
        </div>
    );
};

export default HomeView;
